import moment from 'moment-timezone';

// Import only the organization-supported locales statically
// These are the locales defined in LocaleRegistry.json that organizations can configure
// All other locales will be loaded dynamically on-demand to reduce bundle size
import 'moment/locale/ar'; // Arabic
import 'moment/locale/es'; // Spanish
import 'moment/locale/fr'; // French
import 'moment/locale/pt'; // Portuguese
import 'moment/locale/zh-cn'; // Chinese (Simplified)
import 'moment/locale/sw'; // Swahili
// Note: Somali (so) and Kinyarwanda (rw) are not available in moment, will fall back to English

// Set default locale to English
moment.locale('en');

/**
 * Maps langCode values to moment.js locale codes
 *
 * This map handles special cases where:
 * 1. The langCode doesn't match moment's expected format (e.g., 'zh-CN' → 'zh-cn')
 * 2. The locale isn't available in moment and needs a fallback (mapped to 'en')
 * 3. We want to use a specific regional variant (e.g., 'nb' for Norwegian instead of 'no')
 * 4. Google uses a different code (e.g., 'iw' → 'he' for Hebrew)
 */
export const LOCALE_CODE_MAP: { [key: string]: string } = {
    // Organization-supported locales (these are imported statically above)
    'ar': 'ar',
    'en': 'en',
    'es': 'es-US',
    'es-XL': 'es-US',
    'fr': 'fr',
    'pt': 'pt',
    'zh-CN': 'zh-cn',
    'sw': 'sw',

    // Unsupported in moment - fallback to English
    'rw': 'en',      // Kinyarwanda
    'so': 'en',      // Somali

    // Special mappings
    'no': 'nb',      // Norwegian → Bokmål
    'iw': 'he',      // Hebrew (Google's old code)
    'zh': 'zh-cn',   // Chinese variants
};

// Track which locales we've already attempted to load dynamically
const loadedLocales = new Set<string>(['en', 'es', 'ar', 'fr', 'pt', 'zh-cn', 'sw']);

/**
 * Dynamically imports a moment locale file
 * This creates a separate chunk that's only loaded when needed
 *
 * @param localeCode - The moment.js locale code (e.g., 'vi', 'de', 'ja')
 * @returns Promise that resolves when the locale is loaded
 */
async function loadMomentLocale(localeCode: string): Promise<void> {
    if (loadedLocales.has(localeCode)) {
        return; // Already loaded
    }

    try {
        // Dynamic import creates a separate chunk per locale
        // webpack will automatically handle the code splitting
        await import(
            /* webpackChunkName: "moment-locale-[request]" */
            `moment/locale/${localeCode}.js`
        );
        loadedLocales.add(localeCode);
    } catch (error) {
        // Locale file doesn't exist in moment
        loadedLocales.add(localeCode); 
    }
}


/**
 * Sets the moment.js global locale based on a langCode
 *
 * This function uses a multi-step approach:
 * 1. Checks LOCALE_CODE_MAP for special mappings (e.g., 'zh-CN' → 'zh-cn', 'iw' → 'he')
 * 2. Tries setting the locale (works if already imported statically)
 * 3. If not loaded, dynamically imports the locale file on-demand
 * 4. Falls back to English if the locale is truly unavailable
 *
 * For organization-supported locales (es, ar, fr, pt, zh-cn, sw), the locale is
 * already bundled. For other locales (de, ja, vi, etc.), the locale file is loaded
 * on-demand, reducing initial bundle size.
 *
 * @param langCode - The language code from LocaleRegistry or Google Translate (e.g., 'es', 'ar', 'zh-CN', 'vi')
 * @returns Promise that resolves to the moment.js locale that was set (async), or string (sync if already loaded)
 */
export function setMomentLocale(langCode: string): string | Promise<string> {
    if (!langCode) {
        moment.locale('en');
        return 'en';
    }

    // Check for special mappings first
    const mappedCode = LOCALE_CODE_MAP[langCode] || langCode;

    // Locale not loaded yet - try lowercase version first
    const lowerCode = mappedCode.toLowerCase();

    // Still not loaded - dynamically import it
    // Return a promise that resolves to the locale code
    return loadMomentLocale(lowerCode).then(() => {
        const finalLocale = moment.locale(lowerCode);
        return finalLocale;
    });
}

/**
 * Gets the current moment.js locale
 * @returns The currently active moment.js locale code
 */
export function getMomentLocale(): string {
    return moment.locale();
}
